import { SplineChartWithHeaderProps } from '@features/campaigns/components/Summary/SplineChartWithHeader';
import { CpgReport } from '../../api/schema';
import { formatters } from '@utils/formattersV2';
import { unifiedPlatformColorPalette } from '@utils/unifiedPlatformColorPalette';
import { GLOSSARY } from '@features/glossary/subdomains/cpg/domain/glossary';

const SERIES_NAME = 'All Retailers';

type MinimalCpgReport = Pick<
  CpgReport,
  | 'incrementalSalesLift'
  | 'incrementalSalesLiftConfidenceLevel'
  | 'projectedIncrementalSales'
  | 'totalProjectedSales'
  | 'impressions'
  | 'uniqueUsers'
  | 'reportDate'
  | 'totalSalesRate'
>;

type ChartKey = keyof MinimalCpgReport;

type SummaryChartsPropsOptions = {
  reports: MinimalCpgReport[];
  excludedCharts: ChartKey[];
};

export const summaryChartsProps = (
  options: SummaryChartsPropsOptions
): SplineChartWithHeaderProps[] => {
  const sortedReports = options.reports.sort(
    (a, b) => Date.parse(a.reportDate) - Date.parse(b.reportDate)
  );
  const latestReport = sortedReports.at(-1);

  if (!latestReport) {
    return [];
  }

  const allProps: (SplineChartWithHeaderProps & { key: ChartKey })[] = [
    {
      title: 'Incremental Sales Lift',
      differenceType: 'lift',
      confidenceLevel:
        latestReport.incrementalSalesLiftConfidenceLevel ?? undefined,
      formatter: formatters.lift,
      yAxisLabelFormatter: formatters.percentage,
      data: sortedReports.map(report => ({
        date: report.reportDate,
        value: report.incrementalSalesLift ?? 0,
      })),
      color: unifiedPlatformColorPalette.chartLightGreen,
      seriesName: SERIES_NAME,
      key: 'incrementalSalesLift',
      ['data-testid']: 'incremental-sales-lift-chart',
    },
    {
      title: 'Projected Incremental Sales',
      differenceType: 'standard',
      formatter: value =>
        formatters.currency(value, { full: true, decimals: 0 }),
      yAxisLabelFormatter: formatters.currency,
      data: sortedReports.map(report => ({
        date: report.reportDate,
        value: report.projectedIncrementalSales ?? 0,
      })),
      color: unifiedPlatformColorPalette.chartLightGreen,
      seriesName: SERIES_NAME,
      key: 'projectedIncrementalSales',
      ['data-testid']: 'projected-incremental-sales-chart',
    },
    {
      title: 'Total Projected Sales',
      differenceType: 'standard',
      formatter: value =>
        formatters.currency(value, { full: true, decimals: 0 }),
      yAxisLabelFormatter: formatters.currency,
      data: sortedReports.map(report => ({
        date: report.reportDate,
        value: report.totalProjectedSales ?? 0,
      })),
      color: unifiedPlatformColorPalette.chartLightGreen,
      seriesName: SERIES_NAME,
      key: 'totalProjectedSales',
      ['data-testid']: 'total-projected-sales-chart',
    },
    {
      title: 'Total Sales Rate',
      tooltipTitle: GLOSSARY.salesRate.definition,
      differenceType: 'standard',
      formatter: value => formatters.currency(value, { full: true }),
      yAxisLabelFormatter: formatters.currency,
      data: sortedReports.map(report => ({
        date: report.reportDate,
        value: report.totalSalesRate ?? 0,
      })),
      color: unifiedPlatformColorPalette.chartLightGreen,
      seriesName: SERIES_NAME,
      key: 'totalSalesRate',
      ['data-testid']: 'total-sales-rate-chart',
    },
    {
      title: 'Impressions',
      differenceType: 'standard',
      formatter: value => formatters.impressions(value, { full: true }),
      yAxisLabelFormatter: formatters.impressions,
      data: sortedReports.map(report => ({
        date: report.reportDate,
        value: report.impressions ?? 0,
      })),
      color: unifiedPlatformColorPalette.chartLightGreen,
      seriesName: SERIES_NAME,
      key: 'impressions',
      ['data-testid']: 'impressions-chart',
    },
    {
      title: 'Unique Users',
      differenceType: 'standard',
      formatter: value => formatters.users(value, { full: true }),
      yAxisLabelFormatter: formatters.users,
      data: sortedReports.map(report => ({
        date: report.reportDate,
        value: report.uniqueUsers ?? 0,
      })),
      color: unifiedPlatformColorPalette.chartLightGreen,
      seriesName: SERIES_NAME,
      key: 'uniqueUsers',
      ['data-testid']: 'unique-users-chart',
    },
  ];

  return allProps.filter(prop => !options.excludedCharts.includes(prop.key));
};
